import React from "react";
import { motion } from "framer-motion";
import {
  FaReact,
  FaNodeJs,
  FaDatabase,
  FaGoogle,
  FaApple,
  FaAndroid,
  FaMapMarkedAlt,
  FaExternalLinkAlt,
  FaGithub,
} from "react-icons/fa";
import { SiTypescript, SiRedux, SiMongodb } from "react-icons/si";
import Rlogo from "../assets/images/Rlogo.png";
import RuningTribeH from "../assets/images/RuningTribeH.png";

const projects = [
  {
    title: "RunningTribe",
    company: "Senew Tech",
    desc: "Designed and implemented the complete UI/UX for a fitness tracking application using React Native CLI. Focused on responsive design and seamless navigation.",
    tags: ["React Native CLI", "Firebase", "Maps"],
    color: "from-purple-500 to-indigo-500",
    icons: [FaReact, FaGoogle, FaAndroid],
    links: [
      {
        type: "playstore",
        url: "https://play.google.com/store/apps/details?id=com.runningtribe",
        label: "Google Play",
      },
    ],
    logo: Rlogo,
    screen: RuningTribeH,
  },
  {
    title: "Sabkobol.com",
    company: "Olumi Tech",
    desc: "Business-oriented mobile application for service and order management. Implemented location-based features using Google Maps and managed application state with Redux.",
    tags: ["React Native CLI", "TypeScript", "Redux", "Maps API"],
    color: "from-green-500 to-emerald-500",
    icons: [FaReact, SiTypescript, SiRedux, FaMapMarkedAlt],
    links: [
      {
        type: "playstore",
        url: "https://play.google.com/store/apps/details?id=com.sabkobol&hl=en",
        label: "Google Play",
      },
      {
        type: "appstore",
        url: "https://apps.apple.com/us/app/sabkobol/id6751508730",
        label: "App Store",
      },
    ],
  },
  {
    title: "Riden App",
    company: "Olumi Tech",
    subtitle: "Passenger & Driver App",
    desc: "Mobile applications similar to Uber. Developed passenger and driver mobile applications with ride booking, real-time location tracking and route display using Google Maps API. Integrated Redux for global state management.",
    tags: ["React Native CLI", "TypeScript", "Redux", "Maps API"],
    color: "from-blue-500 to-indigo-500",
    icons: [FaReact, SiTypescript, SiRedux, FaMapMarkedAlt],
    links: [
      {
        type: "experience",
        url: "https://drive.google.com/drive/folders/1zTAADMmlP4ENvGhp_YX0KRjceIiyWKS9?usp=drive_link",
        label: "Experience Letter",
      },
    ],
  },
  {
    title: "Ledgerly App",
    company: "Olumi Tech",
    desc: "Business management and ledger application similar to Zoho Books. Implemented features for managing transactions, accounts, and business records with API integration.",
    tags: ["React Native CLI", "TypeScript", "Redux"],
    color: "from-yellow-500 to-orange-500",
    icons: [FaReact, SiTypescript, SiRedux],
    links: [
      {
        type: "experience",
        url: "https://drive.google.com/drive/folders/1zTAADMmlP4ENvGhp_YX0KRjceIiyWKS9?usp=drive_link",
        label: "Experience Letter",
      },
    ],
  },
  {
    title: "Taskify App",
    company: "Senew Tech",
    desc: "Developed using React Native (Expo) with MongoDB integration. Built task management features with a clean and intuitive UI.",
    tags: ["React Native", "MongoDB", "Node.js"],
    color: "from-pink-500 to-rose-500",
    icons: [FaReact, FaNodeJs, FaDatabase],
    links: [
      {
        type: "github",
        url: "https://github.com/SayedHasanAliShah",
        label: "GitHub",
      },
    ],
  },
  {
    title: "Peasomy",
    company: "Senew Tech",
    desc: "Built using React Native CLI with full API integration using Redux for state management. Developed both front-end UI/UX and connected real-time data from backend services.",
    tags: ["React Native CLI", "Redux", "REST APIs"],
    color: "from-cyan-500 to-blue-500",
    icons: [FaReact, SiRedux, FaNodeJs],
    links: [
      {
        type: "github",
        url: "https://github.com/SayedHasanAliShah",
        label: "GitHub",
      },
    ],
  },
  {
    title: "LiveStreaming App",
    company: "Senew Tech",
    desc: "Interactive live streaming platform with real-time chat and virtual gifting. Developed using React Native CLI with Agora SDK integration for live streaming and Socket.io for real-time communication.",
    tags: ["Agora SDK", "Socket.io", "React Native CLI"],
    color: "from-orange-500 to-red-500",
    icons: [FaReact, FaNodeJs, FaAndroid],
    links: [
      {
        type: "github",
        url: "https://github.com/SayedHasanAliShah",
        label: "GitHub",
      },
    ],
  },
  {
    title: "AdRoadster",
    company: "Software Engineer",
    subtitle: "Mobile App/Website",
    desc: "Mobile application and website designed to revolutionize billboard advertising through targeted ad placement and real-time campaign management. Built using React Native Expo and React for the front end, with Node.js and MongoDB powering the backend.",
    tags: ["Expo", "React", "Node.js", "MongoDB"],
    color: "from-red-500 to-pink-500",
    icons: [FaReact, FaNodeJs, SiMongodb],
    links: [
      {
        type: "github",
        url: "https://github.com/SayedHasanAliShah",
        label: "GitHub",
      },
    ],
  },
];

const LinkButton = ({ link }) => {
  const getIcon = () => {
    switch (link.type) {
      case "playstore":
        return <FaGoogle />;
      case "appstore":
        return <FaApple />;
      case "github":
        return <FaGithub />;
      case "experience":
        return <FaExternalLinkAlt />;
      default:
        return <FaExternalLinkAlt />;
    }
  };

  const getLabel = () => link.label;

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300 font-semibold text-sm border border-white/10 hover:border-white/20"
    >
      {getIcon()}
      {getLabel()}
    </a>
  );
};

const ProjectCard = ({ project, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="group relative grid md:grid-cols-2 gap-8 items-center mb-32 last:mb-0"
  >
    <div
      className={`relative ${index % 2 === 0 ? "md:order-1" : "md:order-2"}`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-neon-purple/20 blur-[100px] rounded-full pointer-events-none" />

      <motion.div
        whileHover={{ scale: 1.02, rotate: index % 2 === 0 ? -2 : 2 }}
        transition={{ duration: 0.4 }}
        className="relative mx-auto w-full max-w-[280px] h-[580px] mobile-frame bg-slate-900 border-slate-800 shadow-2xl"
      >
        <div className="mobile-notch" />
        <div
          className={`h-full w-full bg-gradient-to-br ${project.color} opacity-20`}
        />

        {project.screen ? (
          <div className="absolute inset-0 w-full h-full">
            <img
              src={project.screen}
              alt={`${project.title} screenshot`}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="absolute inset-0 p-6 pt-14 flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="w-8 h-8 rounded-full bg-white/10" />
              <div className="w-20 h-4 rounded-full bg-white/10" />
            </div>
            <div className="h-40 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/20 to-transparent mb-4" />
              <div className="w-3/4 h-4 rounded bg-white/10 mb-2" />
              <div className="w-1/2 h-4 rounded bg-white/10" />
            </div>
            <div className="flex-1 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10" />
          </div>
        )}
      </motion.div>
    </div>

    <div
      className={`${index % 2 === 0 ? "md:order-2" : "md:order-1"} text-left`}
    >
      <motion.div
        initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="flex items-center gap-4 mb-4">
          {project.logo && (
            <img
              src={project.logo}
              alt={`${project.title} logo`}
              className="w-12 h-12 rounded-2xl object-contain bg-slate-950 border border-slate-800/80 p-2 shadow-lg group-hover:scale-105 transition-transform duration-300"
            />
          )}
          <div>
            <h3
              className={`text-4xl font-bold bg-gradient-to-r ${project.color} bg-clip-text text-transparent`}
            >
              {project.title}
            </h3>
            {project.subtitle && (
              <p className="text-slate-500 text-sm">{project.subtitle}</p>
            )}
          </div>
        </div>

        {project.company && (
          <p className="text-accent text-sm font-semibold mb-2">
            {project.company}
          </p>
        )}

        <p className="text-slate-400 text-lg leading-relaxed mb-8">
          {project.desc}
        </p>

        <div className="flex flex-wrap gap-3 mb-8">
          {project.tags.map((tag, i) => (
            <span
              key={i}
              className="px-4 py-1.5 rounded-full text-sm font-medium bg-white/5 border border-white/10 text-slate-300"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex -space-x-3">
            {project.icons.map((Icon, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 z-10"
              >
                <Icon />
              </div>
            ))}
          </div>
          {project.links &&
            project.links.map((link, i) => <LinkButton key={i} link={link} />)}
        </div>
      </motion.div>
    </div>
  </motion.div>
);

export default function Projects() {
  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-32"
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            Featured <span className="text-accent">Work</span>
          </h2>
          <p className="text-slate-400 text-xl max-w-2xl mx-auto">
            A collection of mobile applications designed for performance and
            user experience.
          </p>
        </motion.div>

        <div className="space-y-32">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}
